import React, { forwardRef, useEffect } from 'react';
import { ActionIcon, Box, Group, Text, TextInput, Tooltip } from '@mantine/core';
import { IconSearch, IconX } from '@tabler/icons-react';
import { GANTT_TOOLBAR_BUTTON_RADIUS, GANTT_TOOLBAR_CONTROL_BORDER, ganttToolbarTriggerStyle } from './ganttToolbarStyles';

export interface GanttTaskSearchFieldProps {
  value: string;
  onChange: (value: string) => void;
  /** Optional match count shown when there is an active query. */
  matchCount?: number | null;
  disabled?: boolean;
  /** When false, only the search icon is shown. */
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}

/**
 * Compact toolbar search for task names.
 * Collapsed = icon; expanded = text field. Parent owns ⌘/Ctrl+F focus via the ref.
 */
export const GanttTaskSearchField = forwardRef<HTMLInputElement, GanttTaskSearchFieldProps>(
  function GanttTaskSearchField(
    {
      value,
      onChange,
      matchCount = null,
      disabled,
      expanded = true,
      onExpandedChange,
    },
    ref,
  ) {
    const hasQuery = value.trim().length > 0;

    useEffect(() => {
      if (!expanded) return;
      const input =
        typeof ref === 'object' && ref?.current
          ? ref.current
          : null;
      if (input) {
        window.requestAnimationFrame(() => {
          input.focus();
          input.select();
        });
      }
    }, [expanded, ref]);

    if (!expanded) {
      return (
        <Tooltip label="Search tasks by name (⌘/Ctrl+F)" withArrow>
          <ActionIcon
            variant="default"
            size={28}
            radius={GANTT_TOOLBAR_BUTTON_RADIUS}
            disabled={disabled}
            aria-label="Search tasks by name"
            onClick={() => onExpandedChange?.(true)}
            style={{
              ...ganttToolbarTriggerStyle,
              width: 28,
              padding: 0,
              justifyContent: 'center',
            }}
          >
            <IconSearch size={15} stroke={1.75} />
          </ActionIcon>
        </Tooltip>
      );
    }

    const rightSection = hasQuery ? (
      <ActionIcon
        size="sm"
        variant="subtle"
        color="gray"
        aria-label="Clear search"
        onClick={() => onChange('')}
        disabled={disabled}
      >
        <IconX size={14} stroke={1.75} />
      </ActionIcon>
    ) : (
      <ActionIcon
        size="sm"
        variant="subtle"
        color="gray"
        aria-label="Collapse search"
        onClick={() => {
          if (!hasQuery) onExpandedChange?.(false);
          else onChange('');
        }}
        disabled={disabled}
      >
        <IconX size={14} stroke={1.75} />
      </ActionIcon>
    );

    return (
      <Tooltip
        label="Search by task name. ⌘/Ctrl+F focuses this field."
        multiline
        w={240}
        withArrow
      >
        <Group gap={8} wrap="nowrap" align="center" style={{ minWidth: 180, maxWidth: 320 }}>
          <Box style={{ flex: '1 1 auto', minWidth: 0 }}>
            <TextInput
              ref={ref}
              size="xs"
              placeholder="Search tasks…"
              value={value}
              disabled={disabled}
              onChange={(e) => onChange(e.currentTarget.value)}
              onBlur={() => {
                if (!hasQuery) onExpandedChange?.(false);
              }}
              aria-label="Search tasks by name"
              rightSection={rightSection}
              rightSectionPointerEvents="all"
              styles={{
                input: {
                  height: 28,
                  minHeight: 28,
                  borderRadius: GANTT_TOOLBAR_BUTTON_RADIUS,
                  border: GANTT_TOOLBAR_CONTROL_BORDER,
                  fontSize: 13,
                  fontWeight: 500,
                },
              }}
            />
          </Box>
          {hasQuery && matchCount != null ? (
            <Text
              size="xs"
              c="dimmed"
              style={{ flexShrink: 0, whiteSpace: 'nowrap', lineHeight: 1.2 }}
            >
              {matchCount === 0
                ? 'No matches'
                : `${matchCount} match${matchCount === 1 ? '' : 'es'}`}
            </Text>
          ) : null}
        </Group>
      </Tooltip>
    );
  },
);
